import type { GameConfig, HouseRulesConfig } from '@texas42/game-runtime';
import type {
  AssistanceLevel,
  AnimationSpeed,
  MatchFormat,
  NewGameDraft,
  Ruleset,
  SerializableProductGameConfig,
} from './types.js';
import { validateRuleset, rulesetRequiresHouseRulesLayer } from './ruleset-validation.js';
import {
  STANDARD_RULESET,
  STANDARD_RULESET_ID,
  TOURNAMENT_RULESET,
  TOURNAMENT_RULESET_ID,
} from './standard-ruleset.js';

export interface DraftValidationIssue {
  field: string;
  message: string;
}

export interface DraftValidationResult {
  valid: boolean;
  issues: DraftValidationIssue[];
}

export interface MappedEngineConfig {
  engine: GameConfig;
  serializable: SerializableProductGameConfig;
}

const CORE_LAYERS = new Set(['base', 'consensus']);

/** Layers driven by New Game draft options, not ruleset toggles. */
const DRAFT_CONTROLLED_LAYERS = new Set(['oneHand', 'speed', 'hints']);

export function mapRulesetToLayers(
  ruleset: Ruleset,
  options: {
    matchFormat: MatchFormat;
    assistance: AssistanceLevel;
    animationSpeed: AnimationSpeed;
  },
): string[] {
  const layers = new Set<string>();

  for (const layer of ruleset.enabledLayers) {
    if (!CORE_LAYERS.has(layer) && !DRAFT_CONTROLLED_LAYERS.has(layer)) {
      layers.add(layer);
    }
  }

  if (ruleset.enabledLayers.includes('tournament')) {
    layers.add('tournament');
  }

  if (options.matchFormat === 'practice-hand') {
    layers.add('oneHand');
  }

  if (options.animationSpeed === 'fast') {
    layers.add('speed');
  }

  if (options.assistance === 'suggested-move') {
    layers.add('hints');
  }

  if (ruleset.allPassBehavior === 'force-dealer-30') {
    layers.add('forceDealer30');
  }

  if (rulesetRequiresHouseRulesLayer(ruleset)) {
    layers.add('houseRules');
  }

  layers.add('consensus');

  return [...layers];
}

export function mapRulesetToHouseRules(ruleset: Ruleset): HouseRulesConfig {
  return {
    allPassBehavior: ruleset.allPassBehavior,
    openingBidCapMarks: ruleset.openingBidCapMarks,
    allowFollowMe: ruleset.allowFollowMe,
    allowDoublesTrump: ruleset.allowDoublesTrump,
  };
}

export function resolveDealerSeat(
  dealer: NewGameDraft['dealer'],
  shuffleSeed: number,
): number {
  if (dealer !== 'random') {
    return dealer;
  }
  return shuffleSeed % 4;
}

export function validateNewGameDraft(
  draft: NewGameDraft,
  ruleset: Ruleset | null,
): DraftValidationResult {
  const issues: DraftValidationIssue[] = [];

  if (!ruleset) {
    issues.push({
      field: 'rulesetId',
      message: 'Selected ruleset could not be found.',
    });
    return { valid: false, issues };
  }

  if (draft.rulesetId !== ruleset.id) {
    issues.push({
      field: 'rulesetId',
      message: 'Ruleset selection does not match the loaded ruleset record.',
    });
  }

  const rulesetValidation = validateRuleset(ruleset);
  issues.push(...rulesetValidation.issues);

  return {
    valid: issues.length === 0,
    issues,
  };
}

export function mapDraftToEngineConfig(
  draft: NewGameDraft,
  ruleset: Ruleset,
  shuffleSeed: number,
): MappedEngineConfig {
  const validation = validateNewGameDraft(draft, ruleset);
  if (!validation.valid) {
    const summary = validation.issues.map((issue) => issue.message).join(' ');
    throw new Error(`Invalid new-game draft: ${summary}`);
  }

  const layers = mapRulesetToLayers(ruleset, {
    matchFormat: draft.matchFormat,
    assistance: draft.assistance,
    animationSpeed: draft.animationSpeed,
  });

  const houseRules = rulesetRequiresHouseRulesLayer(ruleset)
    ? mapRulesetToHouseRules(ruleset)
    : undefined;

  const dealer = resolveDealerSeat(draft.dealer, shuffleSeed);

  const engine: GameConfig = {
    playerTypes: ['human', 'ai', 'ai', 'ai'],
    shuffleSeed,
    dealer,
    layers,
    ...(houseRules ? { houseRules } : {}),
  };

  const serializable: SerializableProductGameConfig = {
    rulesetId: ruleset.id,
    matchFormat: draft.matchFormat,
    shuffleSeed,
    dealer,
    layers,
    assistance: draft.assistance,
    animationSpeed: draft.animationSpeed,
    confirmBeforePlay: draft.confirmBeforePlay,
    ...(houseRules ? { houseRules } : {}),
  };

  return { engine, serializable };
}

export function serializableToEngineConfig(
  config: SerializableProductGameConfig,
): GameConfig {
  const builtin =
    config.rulesetId === STANDARD_RULESET_ID
      ? STANDARD_RULESET
      : config.rulesetId === TOURNAMENT_RULESET_ID
        ? TOURNAMENT_RULESET
        : null;
  const layers = builtin
    ? mapRulesetToLayers(builtin, {
        matchFormat: config.matchFormat,
        assistance: config.assistance ?? 'off',
        animationSpeed: config.animationSpeed ?? 'normal',
      })
    : config.layers ??
      (config.matchFormat === 'practice-hand' ? ['oneHand', 'consensus'] : ['consensus']);

  const engine: GameConfig = {
    playerTypes: ['human', 'ai', 'ai', 'ai'],
    ...(config.shuffleSeed !== undefined ? { shuffleSeed: config.shuffleSeed } : {}),
    ...(config.dealer !== undefined ? { dealer: config.dealer } : {}),
    layers,
    ...(config.houseRules ? { houseRules: config.houseRules } : {}),
  };

  return engine;
}

export function isBuiltinRulesetId(id: string): boolean {
  return id === STANDARD_RULESET_ID || id === TOURNAMENT_RULESET_ID;
}

export function buildMatchName(draft: NewGameDraft, ruleset: Ruleset): string {
  const format =
    draft.matchFormat === 'practice-hand'
      ? 'Practice'
      : draft.matchFormat === 'best-of-three'
        ? 'Series'
        : 'Match';
  return `${format} · ${ruleset.name}`;
}
